/**
 * Early Saver Landing Screen
 * Persona-specific landing page for Léa (26 ans, early saver)
 * URL: https://wealfi.co/fr/early-saver
 */

import React, { useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  Linking,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import useDomain from '../../hooks/useDomain';
import useWebSEO from '../../hooks/useWebSEO';
import { analytics, AnalyticsEvent } from '../../utils/analytics';
import { initialUtmParams } from '../../utils/initialUtm';


// Brand Colors with coral accent
const brandColors = {
  coral: '#E07A5F',
  coralLight: '#FFF0EB',
  green: '#71B352',
  greenLight: '#E8F5E0',
  dark: '#3D4543',
  gray: '#6B7280',
  white: '#FFFFFF',
};

// Persona-specific content
const PERSONA = {
  name: 'Léa',
  age: 26,
  job: 'Consultante junior',
  income: '2 200€/mois net',
};

// Recognition checklist for early savers
const recognitionItems = [
  "Tu viens de commencer à gagner ta vie",
  "Tu te demandes quoi faire de ce qui reste à la fin du mois",
  "Tu sens que tu devrais \"investir\" mais tu ne sais pas par où commencer",
  "Tu n'as pas envie de te faire avoir ou de perdre tes économies",
];

// Pain points specific to early savers
const painPoints = [
  {
    emoji: '😅',
    title: "\"Je ne sais pas par où commencer\"",
    description: "PEA, ETF, assurance vie... C'est quoi tout ça ?",
  },
  {
    emoji: '😰',
    title: "\"J'ai peur de me tromper\"",
    description: "Et si je perds mes économies ? C'est normal d'avoir peur.",
  },
  {
    emoji: '🤷‍♀️',
    title: "\"Ce n'est pas pour moi\"",
    description: "L'investissement, c'est pour les riches, non ?",
  },
];

// Features specific to this persona
const features = [
  {
    icon: 'school-outline',
    title: "On t'explique tout",
    desc: "Pas de jargon. Des explications simples, adaptées à ton niveau.",
  },
  {
    icon: 'shield-check-outline',
    title: "À ton rythme",
    desc: "Pas de pression. Tu commences avec ce que tu as, même 50€/mois.",
  },
  {
    icon: 'target',
    title: "Des objectifs concrets",
    desc: "\"Épargner 3 mois de salaire\", \"Préparer un voyage\"... Du concret.",
  },
];

// Testimonials
const testimonials = [
  {
    name: 'Emma K.',
    role: 'RH junior, 25 ans',
    text: "Je ne connaissais rien à la finance. Wealfi m'a expliqué pas à pas. J'ai ouvert mon premier Livret A et mon LEP en une semaine.",
    result: 'LEP ouvert',
  },
  {
    name: 'Lucas D.',
    role: 'Designer, 27 ans',
    text: "Je mettais 200€ sur mon compte courant sans rien en faire. Maintenant j'ai un objectif clair et je sais où va mon argent.",
    result: '1 800€/an',
  },
];

// FAQ items
const faqItems = [
  {
    q: "Je n'ai que 100€ de côté, c'est assez ?",
    a: "Oui ! Wealfi s'adapte à ta situation. L'important c'est de commencer, pas le montant.",
  },
  {
    q: 'Mes données sont en sécurité ?',
    a: "Tu n'es pas obligée de connecter ta banque. Tu peux commencer avec 5 questions simples. Si tu connectes, ça passe par Powens, agréé par l'ACPR. Tes données ne sont jamais vendues.",
  },
  {
    q: '9,99€/mois, ça vaut le coup pour moi ?',
    a: "Le diagnostic est gratuit. Si Wealfi te trouve une optimisation, c'est remboursé. Et tu peux annuler à tout moment.",
  },
];

// Premium features
const premiumFeatures = [
  "Plan d'épargne personnalisé",
  "Suivi d'objectifs illimité",
  "Assistant IA 24/7 sans jugement",
  "Éducation financière complète",
  "Alertes et célébrations",
];

export default function EarlySaverLandingScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { isLandingDomain, appUrl } = useDomain();
  const pageLoadTime = useRef<number>(Date.now());

  useWebSEO({
    title: 'Wealfi — Premiers pas vers l\'épargne | Étudiants & jeunes',
    description: 'Commence à épargner même avec un petit budget. Wealfi t\'accompagne avec un diagnostic IA gratuit et des conseils adaptés à ta situation.',
    keywords: 'épargne débutant, premiers pas finance, budget étudiant, apprendre à épargner, éducation financière gratuit',
    ogUrl: 'https://wealfi.co/fr/early-saver',
    ogImage: 'https://wealfi.co/og-images/early-saver.png',
  });

  // UTM parameters captured at page load (before React Navigation can strip them)
  const getUtmParams = useCallback(() => ({
    ...initialUtmParams,
    persona: 'early_saver',
  }), []);

  // Track page view on mount
  useEffect(() => {
    analytics.initialize();
    analytics.trackEvent(AnalyticsEvent.LANDING_PAGE_VIEWED, {
      ...getUtmParams(),
      referrer: Platform.OS === 'web' ? document.referrer : undefined,
    });

    return () => {
      const timeSpent = Math.floor((Date.now() - pageLoadTime.current) / 1000);
      analytics.trackEvent(AnalyticsEvent.LANDING_TIME_SPENT, {
        seconds: timeSpent,
        ...getUtmParams(),
      });
    };
  }, [getUtmParams]);

  const trackCta = useCallback((ctaName: string, section: string) => {
    analytics.trackEvent(AnalyticsEvent.LANDING_CTA_CLICKED, {
      cta_name: ctaName,
      section,
      ...getUtmParams(),
    });
  }, [getUtmParams]);

  const handleGetStarted = () => {
    trackCta('get_started', 'hero');
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/signup`;
      return;
    }
    navigation.navigate('SignUp');
  };

  const handleLogin = () => {
    trackCta('login', 'navbar');
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/login`;
      return;
    }
    navigation.navigate('Login');
  };

  return (
    <View style={styles.container}>
      {/* Fixed Navigation */}
      <View style={[styles.navbar, isDesktop && styles.navbarDesktop]}>
        <View style={styles.logoContainer}>
          <View style={styles.logoIcon}>
            <Text style={styles.logoEmoji}>🤖</Text>
          </View>
          <Text style={styles.logoText}>
            Wealfi
          </Text>
        </View>

        <View style={styles.navActions}>
          {isDesktop && (
            <>
              <Pressable onPress={() => { if (Platform.OS === 'web') window.location.href = '/fr/tools'; }} style={styles.navLinkButton}>
                <Text style={styles.navLinkText}>Outils</Text>
              </Pressable>
              <Pressable onPress={handleLogin} style={styles.navLinkButton}>
                <Text style={styles.navLinkText}>Connexion</Text>
              </Pressable>
            </>
          )}
          <Pressable onPress={handleGetStarted} style={[styles.ctaButton, !isDesktop && styles.ctaButtonMobile]}>
            <Text style={styles.ctaButtonText}>{isDesktop ? 'Commencer gratuitement' : 'Essayer'}</Text>
          </Pressable>
        </View>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Hero Section */}
        <View style={[styles.heroSection, isDesktop && styles.heroSectionDesktop]}>
          <View style={[styles.heroContent, isDesktop && styles.heroContentDesktop]}>
            <View style={[styles.heroLeft, isDesktop && styles.heroLeftDesktop]}>
              <View style={styles.heroBadge}>
                <Text style={styles.heroBadgeEmoji}>🌱</Text>
                <Text style={styles.heroBadgeText}>Pour celles et ceux qui débutent</Text>
              </View>

              <Text style={[styles.heroTitle, isDesktop && styles.heroTitleDesktop]}>
                Tu ne sais pas par où commencer ?{'\n'}
                <Text style={styles.heroTitleAccent}>C'est normal.</Text>
              </Text>

              <Text style={styles.heroSubtitle}>
                Wealfi t'accompagne pas à pas, sans jargon, sans jugement.
                En 3 minutes, tu sais exactement quoi faire.
              </Text>

              <Pressable onPress={handleGetStarted} style={styles.primaryButton}>
                <Text style={styles.primaryButtonText}>Faire mon premier diagnostic</Text>
                <Icon name="arrow-right" size={20} color={brandColors.white} />
              </Pressable>

              <View style={styles.heroTrust}>
                <View style={styles.trustItem}>
                  <Icon name="heart-outline" size={18} color={brandColors.coral} />
                  <Text style={styles.trustText}>Bienveillant</Text>
                </View>
                <View style={styles.trustItem}>
                  <Icon name="school-outline" size={18} color={brandColors.coral} />
                  <Text style={styles.trustText}>Pédagogue</Text>
                </View>
                <View style={styles.trustItem}>
                  <Icon name="shield-check" size={18} color={brandColors.coral} />
                  <Text style={styles.trustText}>Sans risque</Text>
                </View>
              </View>
            </View>

            {/* Goals Preview - Desktop only */}
            {isDesktop && (
              <View style={styles.heroRight}>
                <View style={styles.goalsPreview}>
                  <View style={styles.goalsHeader}>
                    <Text style={styles.goalsTitle}>🎯 Plan de {PERSONA.name}</Text>
                    <View style={styles.goalsBadge}>
                      <Text style={styles.goalsBadgeText}>Débutante</Text>
                    </View>
                  </View>

                  <View style={styles.goalsList}>
                    <View style={styles.goalItem}>
                      <View style={styles.goalCheck}>
                        <Icon name="check" size={16} color={brandColors.white} />
                      </View>
                      <View style={styles.goalContent}>
                        <Text style={styles.goalTitle}>Constituer une épargne de précaution</Text>
                        <Text style={styles.goalProgress}>3 mois de salaire → 6 600€</Text>
                      </View>
                    </View>
                    <View style={styles.goalItem}>
                      <View style={[styles.goalCheck, styles.goalCheckPending]}>
                        <Text style={styles.goalCheckNumber}>2</Text>
                      </View>
                      <View style={styles.goalContent}>
                        <Text style={styles.goalTitle}>Ouvrir un LEP (meilleur taux)</Text>
                        <Text style={styles.goalProgress}>En cours...</Text>
                      </View>
                    </View>
                    <View style={styles.goalItem}>
                      <View style={[styles.goalCheck, styles.goalCheckLocked]}>
                        <Icon name="lock" size={14} color={brandColors.gray} />
                      </View>
                      <View style={styles.goalContent}>
                        <Text style={[styles.goalTitle, styles.goalTitleLocked]}>Premier investissement</Text>
                        <Text style={styles.goalProgress}>Après l'épargne de précaution</Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Recognition Section */}
        <View style={styles.recognitionSection}>
          <Text style={styles.sectionTitle}>Tu te reconnais ?</Text>
          <View style={[styles.recognitionGrid, isDesktop && styles.recognitionGridDesktop]}>
            {recognitionItems.map((item, index) => (
              <View key={index} style={styles.recognitionItem}>
                <Icon name="checkbox-marked-circle" size={24} color={brandColors.coral} />
                <Text style={styles.recognitionText}>{item}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Pain Points Section */}
        <View style={styles.painSection}>
          <Text style={styles.sectionTitle}>On te comprend</Text>
          <View style={[styles.painGrid, isDesktop && styles.painGridDesktop]}>
            {painPoints.map((pain, index) => (
              <View key={index} style={styles.painCard}>
                <Text style={styles.painEmoji}>{pain.emoji}</Text>
                <Text style={styles.painTitle}>{pain.title}</Text>
                <Text style={styles.painDesc}>{pain.description}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Pourquoi commencer maintenant */}
        <View style={styles.whyNowSection}>
          <Text style={styles.sectionTitle}>
            Pourquoi commencer <Text style={styles.titleAccentCoral}>maintenant</Text> ?
          </Text>
          <View style={[styles.whyNowGrid, isDesktop && styles.whyNowGridDesktop]}>
            {[
              {
                emoji: '🏠',
                title: 'Un appart à toi',
                desc: "Chaque mois d'attente, c'est de l'épargne en moins pour ton apport.",
              },
              {
                emoji: '🛡️',
                title: 'Protéger tes proches',
                desc: "Un fonds d'urgence, c'est la liberté de dire non à un imprévu.",
              },
              {
                emoji: '✈️',
                title: 'Vivre tes projets',
                desc: "Voyage, formation, changement de vie... L'argent ne devrait pas être un frein.",
              },
            ].map((card, index) => (
              <View key={index} style={styles.whyNowCard}>
                <Text style={styles.whyNowEmoji}>{card.emoji}</Text>
                <Text style={styles.whyNowCardTitle}>{card.title}</Text>
                <Text style={styles.whyNowCardDesc}>{card.desc}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Solution Section */}
        <View style={styles.solutionSection}>
          <Text style={styles.sectionTitle}>
            Wealfi t'accompagne <Text style={styles.titleAccent}>pas à pas</Text>
          </Text>
          <Text style={styles.sectionSubtitle}>
            Pas besoin de tout savoir. On t'explique au fur et à mesure.
          </Text>

          <View style={[styles.featuresGrid, isDesktop && styles.featuresGridDesktop]}>
            {features.map((feature, index) => (
              <View key={index} style={styles.featureCard}>
                <View style={styles.featureIconContainer}>
                  <Icon name={feature.icon as any} size={32} color={brandColors.coral} />
                </View>
                <Text style={styles.featureTitle}>{feature.title}</Text>
                <Text style={styles.featureDesc}>{feature.desc}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Social Proof Section */}
        <View style={styles.socialProofSection}>
          <Text style={styles.sectionTitle}>Elles ont commencé comme toi</Text>
          <View style={[styles.testimonialsGrid, isDesktop && styles.testimonialsGridDesktop]}>
            {testimonials.map((t, index) => (
              <View key={index} style={styles.testimonialCard}>
                <View style={styles.testimonialHeader}>
                  <View style={styles.testimonialAvatar}>
                    <Text style={styles.testimonialAvatarText}>{t.name.charAt(0)}</Text>
                  </View>
                  <View style={styles.testimonialInfo}>
                    <Text style={styles.testimonialName}>{t.name}</Text>
                    <Text style={styles.testimonialRole}>{t.role}</Text>
                  </View>
                  <View style={styles.testimonialResult}>
                    <Text style={styles.testimonialResultValue}>{t.result}</Text>
                  </View>
                </View>
                <Text style={styles.testimonialText}>"{t.text}"</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Trust Section */}
        <View style={styles.trustSection}>
          <Text style={styles.sectionTitle}>
            Tes données sont <Text style={styles.titleAccentCoral}>protégées</Text>
          </Text>

          <View style={[styles.trustGrid, isDesktop && styles.trustGridDesktop]}>
            {[
              {
                icon: 'lock',
                title: 'Connexion sécurisée',
                desc: 'Tes échanges sont protégés par chiffrement SSL/TLS',
              },
              {
                icon: 'hand-back-right',
                title: 'Données jamais vendues',
                desc: 'Pas de pub ciblée, pas de revente à des tiers',
              },
              {
                icon: 'bank-outline',
                title: 'Connexion bancaire optionnelle',
                desc: 'Via Powens, établissement de paiement agréé ACPR (n°16948)',
              },
              {
                icon: 'delete-outline',
                title: 'Supprime ton compte à tout moment',
                desc: 'Un clic pour supprimer toutes tes données',
              },
            ].map((card, index) => (
              <View key={index} style={[styles.trustCard, isDesktop && styles.trustCardDesktop]}>
                <View style={styles.trustCardIcon}>
                  <Icon name={card.icon as any} size={28} color={brandColors.coral} />
                </View>
                <Text style={styles.trustCardTitle}>{card.title}</Text>
                <Text style={styles.trustCardDesc}>{card.desc}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* FAQ Section */}
        <View style={styles.faqSection}>
          <Text style={styles.sectionTitle}>Questions fréquentes</Text>
          <View style={styles.faqGrid}>
            {faqItems.map((faq, index) => (
              <View key={index} style={styles.faqCard}>
                <Text style={styles.faqQuestion}>{faq.q}</Text>
                <Text style={styles.faqAnswer}>{faq.a}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Pricing Section */}
        <View style={styles.pricingSection}>
          <Text style={styles.sectionTitle}>Moins qu'un brunch 🥐</Text>
          <View style={styles.pricingCard}>
            <View style={styles.earlyBadge}>
              <Text style={styles.earlyBadgeText}>🎉 EARLY ACCESS -20%</Text>
            </View>
            <Text style={styles.pricingLabel}>Premium</Text>
            <View style={styles.priceRow}>
              <Text style={styles.priceOriginal}>12,99€</Text>
              <Text style={styles.priceValue}>9,99€</Text>
              <Text style={styles.priceUnit}>/mois</Text>
            </View>
            <Text style={styles.priceSubtext}>Annulable à tout moment</Text>

            <View style={styles.featuresList}>
              {premiumFeatures.map((feat, i) => (
                <View key={i} style={styles.premiumFeatureItem}>
                  <Text style={styles.premiumFeatureCheck}>✓</Text>
                  <Text style={styles.premiumFeatureText}>{feat}</Text>
                </View>
              ))}
            </View>

            <Pressable
              style={styles.pricingCta}
              onPress={() => { trackCta('start_premium', 'pricing'); handleGetStarted(); }}
            >
              <Text style={styles.pricingCtaText}>Commencer gratuitement</Text>
            </Pressable>
            <Text style={styles.pricingNote}>Le diagnostic est 100% gratuit</Text>
            <Text style={styles.pricingDisclaimer}>
              Wealfi est un outil d'aide à la décision. Ce n'est pas un service de conseil en investissement au sens de l'AMF.
            </Text>
          </View>
        </View>

        {/* Final CTA */}
        <View style={styles.finalCtaSection}>
          <Text style={styles.finalCtaTitle}>
            Le plus dur, c'est de commencer.{'\n'}On est là pour ça. 💪
          </Text>
          <Pressable
            style={styles.finalCtaButton}
            onPress={() => { trackCta('final_cta', 'footer'); handleGetStarted(); }}
          >
            <Text style={styles.finalCtaButtonText}>Faire mon premier diagnostic</Text>
            <Icon name="arrow-right" size={20} color={brandColors.white} />
          </Pressable>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerLogo}>
            Wealfi
          </Text>
          <Text style={styles.footerTagline}>
            Ton assistant financier bienveillant
          </Text>
          <View style={styles.footerToolsSection}>
            <Text style={styles.footerToolsTitle}>Outils Gratuits</Text>
            <Pressable onPress={() => { if (Platform.OS === 'web') window.location.href = '/fr/tools'; }}>
              <Text style={styles.footerToolsLink}>Tous les outils</Text>
            </Pressable>
            <Pressable onPress={() => { if (Platform.OS === 'web') window.location.href = '/fr/tools/compound-interest-calculator'; }}>
              <Text style={styles.footerToolsLink}>Intérêts Composés</Text>
            </Pressable>
          </View>
          <Text style={styles.footerDisclaimer}>
            © 2026 Wealfi • Pas un service de conseil en investissement au sens de l'AMF
          </Text>
        </View>
      </ScrollView>

    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: brandColors.white,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 70,
  },

  // Navbar
  navbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  navbarDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 16,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: brandColors.coralLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoEmoji: {
    fontSize: 20,
  },
  logoText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
  },
  logoTextAccent: {
    color: brandColors.coral,
  },
  navActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  navLinkButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  navLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.gray,
  },
  ctaButton: {
    backgroundColor: brandColors.coral,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
  },
  ctaButtonMobile: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  ctaButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },

  // Hero
  heroSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    backgroundColor: brandColors.white,
  },
  heroSectionDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 80,
  },
  heroContent: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
  },
  heroContentDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 48,
  },
  heroLeft: {
    flex: 1,
    alignItems: 'center',
  },
  heroLeftDesktop: {
    alignItems: 'flex-start',
  },
  heroRight: {
    flex: 1,
  },
  heroBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: brandColors.coralLight,
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginBottom: 24,
    gap: 8,
  },
  heroBadgeEmoji: {
    fontSize: 16,
  },
  heroBadgeText: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.coral,
  },
  heroTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    lineHeight: 40,
    marginBottom: 16,
  },
  heroTitleDesktop: {
    fontSize: 44,
    lineHeight: 52,
    textAlign: 'left',
  },
  heroTitleAccent: {
    color: brandColors.coral,
  },
  heroSubtitle: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: 32,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: brandColors.coral,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    gap: 8,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.white,
  },
  heroTrust: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
    marginTop: 24,
  },
  trustItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  trustText: {
    fontSize: 13,
    color: brandColors.gray,
  },

  // Goals Preview
  goalsPreview: {
    backgroundColor: '#FAFBFC',
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  goalsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  goalsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: brandColors.dark,
  },
  goalsBadge: {
    backgroundColor: brandColors.coralLight,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  goalsBadgeText: {
    fontSize: 12,
    fontWeight: '500',
    color: brandColors.coral,
  },
  goalsList: {
    gap: 16,
  },
  goalItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  goalCheck: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: brandColors.green,
    justifyContent: 'center',
    alignItems: 'center',
  },
  goalCheckPending: {
    backgroundColor: brandColors.coral,
  },
  goalCheckNumber: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },
  goalCheckLocked: {
    backgroundColor: '#E5E7EB',
  },
  goalContent: {
    flex: 1,
  },
  goalTitle: {
    fontSize: 15,
    fontWeight: '500',
    color: brandColors.dark,
    marginBottom: 2,
  },
  goalTitleLocked: {
    color: brandColors.gray,
  },
  goalProgress: {
    fontSize: 13,
    color: brandColors.gray,
  },

  // Recognition Section
  recognitionSection: {
    backgroundColor: '#FFF9F7',
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  recognitionGrid: {
    width: '100%',
    maxWidth: 600,
    gap: 16,
  },
  recognitionGridDesktop: {
    maxWidth: 800,
  },
  recognitionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: brandColors.white,
    padding: 16,
    borderRadius: 12,
  },
  recognitionText: {
    flex: 1,
    fontSize: 15,
    color: brandColors.dark,
    lineHeight: 22,
  },

  // Pain Section
  painSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  painGrid: {
    width: '100%',
    maxWidth: 1000,
    gap: 16,
  },
  painGridDesktop: {
    flexDirection: 'row',
  },
  painCard: {
    flex: 1,
    backgroundColor: brandColors.coralLight,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  painEmoji: {
    fontSize: 40,
    marginBottom: 12,
  },
  painTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
    textAlign: 'center',
  },
  painDesc: {
    fontSize: 14,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Why Now Section
  whyNowSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
    backgroundColor: brandColors.coralLight,
  },
  whyNowGrid: {
    marginTop: 24,
    gap: 16,
    width: '100%',
  },
  whyNowGridDesktop: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
  },
  whyNowCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    flex: 1,
    minWidth: 200,
  },
  whyNowEmoji: {
    fontSize: 36,
    marginBottom: 12,
  },
  whyNowCardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
    textAlign: 'center',
  },
  whyNowCardDesc: {
    fontSize: 14,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Solution Section
  solutionSection: {
    backgroundColor: '#FFF9F7',
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    marginBottom: 12,
  },
  sectionSubtitle: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    marginBottom: 32,
    maxWidth: 600,
  },
  titleAccent: {
    color: brandColors.coral,
  },
  featuresGrid: {
    width: '100%',
    maxWidth: 1000,
    gap: 16,
  },
  featuresGridDesktop: {
    flexDirection: 'row',
  },
  featureCard: {
    flex: 1,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  featureIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: brandColors.coralLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  featureTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
    textAlign: 'center',
  },
  featureDesc: {
    fontSize: 14,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Social Proof
  socialProofSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  testimonialsGrid: {
    width: '100%',
    maxWidth: 1000,
    gap: 16,
  },
  testimonialsGridDesktop: {
    flexDirection: 'row',
  },
  testimonialCard: {
    flex: 1,
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  testimonialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  testimonialAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: brandColors.coral,
    justifyContent: 'center',
    alignItems: 'center',
  },
  testimonialAvatarText: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.white,
  },
  testimonialInfo: {
    flex: 1,
  },
  testimonialName: {
    fontSize: 15,
    fontWeight: '600',
    color: brandColors.dark,
  },
  testimonialRole: {
    fontSize: 13,
    color: brandColors.gray,
  },
  testimonialResult: {
    backgroundColor: brandColors.coralLight,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  testimonialResultValue: {
    fontSize: 13,
    fontWeight: '600',
    color: brandColors.coral,
  },
  testimonialText: {
    fontSize: 15,
    color: brandColors.gray,
    lineHeight: 24,
    fontStyle: 'italic',
  },

  // FAQ
  faqSection: {
    backgroundColor: '#FFF9F7',
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  faqGrid: {
    width: '100%',
    maxWidth: 700,
    gap: 16,
  },
  faqCard: {
    backgroundColor: brandColors.white,
    borderRadius: 12,
    padding: 20,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.dark,
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 22,
  },

  // Pricing
  pricingSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    alignItems: 'center',
  },
  pricingCard: {
    backgroundColor: brandColors.white,
    borderRadius: 20,
    padding: 32,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: brandColors.coral,
  },
  earlyBadge: {
    backgroundColor: brandColors.coral,
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 16,
    marginBottom: 16,
  },
  earlyBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: brandColors.white,
  },
  pricingLabel: {
    fontSize: 24,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 8,
  },
  priceOriginal: {
    fontSize: 18,
    color: brandColors.gray,
    textDecorationLine: 'line-through',
  },
  priceValue: {
    fontSize: 48,
    fontWeight: '700',
    color: brandColors.coral,
  },
  priceUnit: {
    fontSize: 18,
    color: brandColors.gray,
  },
  priceSubtext: {
    fontSize: 14,
    color: brandColors.gray,
    marginBottom: 24,
  },
  featuresList: {
    width: '100%',
    gap: 12,
    marginBottom: 24,
  },
  premiumFeatureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  premiumFeatureCheck: {
    fontSize: 16,
    color: brandColors.coral,
    fontWeight: '600',
  },
  premiumFeatureText: {
    fontSize: 15,
    color: brandColors.dark,
  },
  pricingCta: {
    backgroundColor: brandColors.coral,
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 32,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  pricingCtaText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.white,
  },
  pricingNote: {
    fontSize: 13,
    color: brandColors.gray,
    textAlign: 'center',
  },
  pricingDisclaimer: {
    fontSize: 12,
    color: brandColors.gray,
    textAlign: 'center',
    marginTop: 16,
    fontStyle: 'italic',
    paddingHorizontal: 24,
    lineHeight: 18,
  },

  // Final CTA
  finalCtaSection: {
    backgroundColor: brandColors.coral,
    paddingHorizontal: 16,
    paddingVertical: 64,
    alignItems: 'center',
  },
  finalCtaTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.white,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 36,
  },
  finalCtaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: brandColors.white,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 32,
    gap: 8,
  },
  finalCtaButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.coral,
  },

  // Trust Section
  trustSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    backgroundColor: brandColors.coralLight,
  },
  trustGrid: {
    marginTop: 24,
    gap: 16,
  },
  trustGridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 20,
  },
  trustCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  trustCardDesktop: {
    flex: 1,
    minWidth: 220,
    maxWidth: 280,
  },
  trustCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: brandColors.coralLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  trustCardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  trustCardDesc: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 20,
  },
  titleAccentCoral: {
    color: brandColors.coral,
  },

  // Footer
  footer: {
    backgroundColor: brandColors.dark,
    paddingHorizontal: 16,
    paddingVertical: 32,
    alignItems: 'center',
  },
  footerLogo: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.white,
    marginBottom: 8,
  },
  footerTagline: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
    marginBottom: 16,
  },
  footerToolsSection: {
    alignItems: 'center',
    marginBottom: 16,
    gap: 6,
  },
  footerToolsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: 'rgba(255,255,255,0.8)',
    marginBottom: 4,
  },
  footerToolsLink: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
  footerDisclaimer: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
  },
});
